import { useEffect, useState } from 'react'

import { useIncidentCreationData } from '../hooks/useIncidentCreationData'
import { UseFormMethods } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { Box, ModalBody, Text } from '@chakra-ui/react'

import {
  FormInputControl,
  FormInputSelectControl,
  FormTextareaControl,
} from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'
import {
  getIncidentCategoryOptions,
  getIncidentModalFacilityOptions,
} from '@/utils/forms/optionBuilders'
import {
  MISCELLANEOUS,
  sortOptions,
  sortOptionsWithLabelLast,
} from '@/utils/forms/optionHelpers'
import {
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import { FormInputsIProps } from '../types/types'
import { MAX_INCIDENT_IMAGES } from '../utils/constants'
import {
  descriptionRules,
  facilityNameRules,
  incidentCategoryRules,
  nameRules,
} from '../utils/utils'
import { AttachImagesButton } from './AttachImagesButton'
import { MarketIncidentForm } from './MarketIncidentForm'
import { NormalIncidentForm } from './NormalIncidentForm'
import { SelectedImagesPreview } from './SelectedImagesPreview'

interface IncidentModalBodyIProps {
  form: UseFormMethods<FormInputsIProps>
  selectedImages: File[]
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  onImageRemove: (file: File) => void
}

export const IncidentModalBody = ({
  form,
  selectedImages,
  onImageSelect,
  onImageRemove,
}: IncidentModalBodyIProps) => {
  const { watch, register, getValues, reset, control, errors } = form
  const [facility, setFacility] = useState(null)
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const {
    facilitiesData,
    isFacilitiesLoading,
    customFacilitiesFilter,
    isSearchFacilityByAddressEnabled,
    categoriesData,
  } = useIncidentCreationData({
    facilityName: debouncedfacilityName,
  })
  const facilityOptions = getIncidentModalFacilityOptions(
    facilitiesData,
    isSearchFacilityByAddressEnabled
  )
  const incidentCategories = sortOptionsWithLabelLast(
    sortOptions(getIncidentCategoryOptions(categoriesData)),
    MISCELLANEOUS
  )

  const watchFacilityOrigin = watch('facilityOrigin')
  const shouldShowMarketFlow = facility?.type === FacilityType.Market

  useEffect(() => {
    if (watchFacilityOrigin) {
      const selectedFacility = facilitiesData?.facilities?.edges?.find(
        (f) => f?.node?.id == watchFacilityOrigin?.value
      )
      setFacility(selectedFacility?.node)
      reset({
        ...getValues(),
        floorOrigin: null,
        deviceOrigin: null,
      })
    }
  }, [watchFacilityOrigin])

  return (
    <ModalBody p={0}>
      <Box px={6} py={2}>
        <Box mb={3}>
          <FormInputControl
            data-testid='createIncidentModal_incidentName'
            errorMessage={errors.name && errors.name.message}
            id='name'
            inputRef={register(nameRules)}
            isInvalid={!!errors.name}
            label='Incident Name (optional)'
            placeholder='Enter Incident Name'
          />
        </Box>
        <Box mb={3}>
          <FormInputSelectControl
            control={control}
            dataTestId='createIncidentModal_facilityName'
            defaultValue={null}
            // Filters by facility name and address (when searchFacilityByAddress FF is enabled).
            // Without filterOption, React Select defaults to label-only search.
            // To modify searchable fields, update customFacilitiesFilter.
            // https://react-select.com/advanced
            filterOption={customFacilitiesFilter}
            id='facilityOrigin'
            isInvalid={!!errors.facilityOrigin}
            isLoading={isFacilitiesLoading}
            label='Facility Name *'
            noOptionsMessage={({ inputValue }) =>
              getFacilityNoOptionsMessage(inputValue)
            }
            onInputChange={setFacilityName}
            options={facilityOptions}
            placeholder='Select the Facility of Origin'
            rules={facilityNameRules}
          />
        </Box>
        <Box mb={3}>
          <FormInputSelectControl
            control={control}
            dataTestId='createIncidentModal_incidentCategory'
            defaultValue={null}
            id='incidentCategory'
            isInvalid={!!errors.incidentCategory}
            label='Incident Category *'
            options={incidentCategories}
            placeholder='Select Incident Category'
            rules={incidentCategoryRules}
          />
        </Box>
        {shouldShowMarketFlow ? (
          <MarketIncidentForm form={form} />
        ) : (
          <NormalIncidentForm facility={facility} form={form} />
        )}
        <Box mb={3}>
          <FormTextareaControl
            data-testid='createIncidentModal_description'
            errorMessage={errors.description && errors.description.message}
            id='description'
            isInvalid={!!errors.description}
            label='Incident Description (optional)'
            labelFontWeight='500'
            placeholder='Enter Incident Description'
            textareaRef={register(descriptionRules)}
          />
        </Box>
        <Box mb={3}>
          <Text
            color='gray.800'
            fontSize='md'
            fontWeight='medium'
            mb={2}
            opacity={0.5}
          >
            Attachments (optional)
          </Text>
          <AttachImagesButton
            handleFileSelect={onImageSelect}
            maxFiles={MAX_INCIDENT_IMAGES}
            selectedFiles={selectedImages}
          />
          {selectedImages.length > 0 && (
            <SelectedImagesPreview
              removeFile={onImageRemove}
              selectedFiles={selectedImages}
            />
          )}
        </Box>
      </Box>
    </ModalBody>
  )
}
